import { createSignal } from 'solid-js';

export type History<T extends object> = {
  readonly items: ReadonlyArray<T>;
  readonly index: Readonly<number>;
  back: () => T | null;
  forward: () => T | null;
  canGoBack: () => boolean;
  canGoForward: () => boolean;
  push: (next: T) => void;
  merge: (next: T) => void;
  /**
   * Replace the item at the current index in-place without changing the index
   * or truncating forward entries. Used to update an entry's mutable state
   * (e.g. captured per-entry state) before navigating away.
   */
  replaceCurrent: (next: T) => void;
  /**
   * Jump the current index to `n` (clamped to the valid range). Returns the
   * item at that position, or null if the history is empty. Use this to
   * navigate to a known prior entry in one step rather than walking via
   * `back()`/`forward()`.
   */
  goToIndex: (n: number) => T | null;
  remove: (predicate: (item: T) => boolean) => T | null;
};

const inc = (x: number) => x + 1;
const dec = (x: number) => x - 1;

export function createHistory<T extends object>(): History<T> {
  let items: T[] = [];
  const [index, setIndex] = createSignal(-1);

  const canGoBack = () => {
    return index() > 0;
  };

  const canGoForward = () => {
    return index() < items.length - 1;
  };

  const isAtEnd = () => {
    if (items.length === 0) return true;
    return index() === items.length - 1;
  };

  const push = (next: T) => {
    if (!isAtEnd()) {
      fork(next);
      return;
    }
    items.push(next);
    setIndex(inc);
  };

  const merge = (next: T) => {
    items.splice(index(), items.length - index());
    items.push(next);
  };

  const replaceCurrent = (next: T) => {
    const i = index();
    if (i < 0 || i >= items.length) return;
    items[i] = next;
  };

  const fork = (next: T) => {
    items.splice(index() + 1, items.length - index() - 1);
    items.push(next);
    setIndex(inc);
  };

  const back = () => {
    if (!canGoBack()) return null;
    setIndex(dec);
    return items[index()];
  };

  const forward = () => {
    if (!canGoForward()) return null;
    setIndex(inc);
    return items[index()];
  };

  const goToIndex = (n: number) => {
    if (items.length === 0) return null;
    const clamped = Math.max(0, Math.min(items.length - 1, n));
    setIndex(clamped);
    return items[clamped];
  };

  const remove = (predicate: (item: T) => boolean) => {
    const prevItems = items;
    const prevIndex = index();

    let newIndex = prevIndex;
    const nextItems: T[] = [];

    for (let i = 0; i < prevItems.length; i++) {
      const item = prevItems[i];
      if (predicate(item)) {
        if (i < prevIndex) {
          newIndex -= 1;
        }
        continue;
      }
      nextItems.push(item);
    }

    if (nextItems.length === 0) {
      items = [];
      setIndex(-1);
      return null;
    }

    if (newIndex >= nextItems.length) {
      newIndex = nextItems.length - 1;
    }

    items = nextItems;
    setIndex(newIndex);
    return items[newIndex] ?? null;
  };
  return {
    get items() {
      return items;
    },
    get index() {
      return index();
    },
    back,
    push,
    merge,
    replaceCurrent,
    goToIndex,
    forward,
    canGoBack,
    canGoForward,
    remove,
  };
}
